import { useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Md } from "../components/Markdown.tsx";
import { Badge, Button, Card, cx, ErrorText, PageHeader, Spinner } from "../components/ui.tsx";
import { relative } from "../lib/cron.ts";
import { Icon } from "../components/icons.tsx";

export type Approval = Awaited<ReturnType<typeof fetchApprovals>>["approvals"][number];

export const fetchApprovals = (q: { status?: "pending"; sessionId?: string } = {}) => unwrap(api.me.approvals.$get({ query: q }));

/** Pending approvals, for the nav badge (polled). */
export const usePendingApprovals = () =>
  useQuery({ queryKey: ["approvals", "count"], queryFn: () => unwrap(api.me.approvals.count.$get()), refetchInterval: 20_000 });

const CHANNEL: Record<string, string> = { web: "the web app", slack: "Slack", discord: "Discord", whatsapp: "WhatsApp" };

export function useDecide(onDone?: (a: Approval) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; decision: "approve" | "reject"; note?: string }) =>
      unwrap(api.me.approvals[":id"].decide.$post({ param: { id: String(v.id) }, json: { decision: v.decision, note: v.note } })),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["approvals"] });
      onDone?.(r.approval);
    },
  });
}

/** One approval: what the assistant prepared, and Approve / Reject (with an optional note). */
export function ApprovalCard({ a, compact, onDecided }: { a: Approval; compact?: boolean; onDecided?: (a: Approval) => void }) {
  const decide = useDecide(onDecided);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [open, setOpen] = useState(!compact || a.status === "pending");
  const pending = a.status === "pending";
  return (
    <Card className={cx("overflow-hidden", pending && "border-amber-400/70 ring-1 ring-amber-400/30")}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-start gap-3 px-4 py-3 text-left">
        <span className={cx("mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full", pending ? "bg-amber-500/12 text-amber-700" : a.status === "approved" ? "bg-emerald-500/12 text-emerald-700" : a.status === "rejected" ? "bg-red-500/10 text-red-700" : "bg-[var(--panel-2)] text-[var(--muted)]")}>
          <Icon name={pending ? "bell" : a.status === "approved" ? "check" : a.status === "rejected" ? "x" : "stopCircle"} size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{a.title}</span>
            <Badge tone={pending ? "accent" : a.status === "approved" ? "good" : "bad"}>{pending ? "Waiting for you" : a.status}</Badge>
            {a.kind === "colleague_reply" && <Badge>Colleague request</Badge>}
          </div>
          <div className="mt-0.5 text-xs text-[var(--muted)]">
            #{a.id} · asked {relative(a.createdAt)}
            {a.decidedAt && ` · ${a.status} ${relative(a.decidedAt)} in ${CHANNEL[a.decidedVia ?? "web"]}`}
            {pending && a.channels.length > 0 && ` · also sent to ${a.channels.map((c) => CHANNEL[c]).join(", ")}`}
          </div>
        </div>
        <Icon name="chevron" size={16} className={cx("mt-1.5 shrink-0 text-[var(--muted)] transition-transform", open ? "-rotate-90" : "rotate-90")} />
      </button>
      {open && (
        <div className="border-t border-[var(--line)] px-4 py-3">
          <div className="prose-krut max-h-[28rem] overflow-y-auto text-sm">
            <Md>{a.body}</Md>
          </div>
          {a.note && <p className="mt-3 rounded-lg bg-[var(--panel-2)] px-3 py-2 text-sm">Your note: {a.note}</p>}
          {pending && (
            <div className="mt-4 space-y-2">
              {showNote && (
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder="Optional note for your assistant (e.g. “send it tomorrow instead”)"
                  className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 text-sm"
                />
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="primary" disabled={decide.isPending} onClick={() => decide.mutate({ id: a.id, decision: "approve", note: note || undefined })}>
                  Approve
                </Button>
                <Button disabled={decide.isPending} onClick={() => decide.mutate({ id: a.id, decision: "reject", note: note || undefined })}>
                  Reject
                </Button>
                {!showNote && (
                  <button className="text-xs text-[var(--muted)] hover:text-[var(--text)]" onClick={() => setShowNote(true)}>
                    Add a note
                  </button>
                )}
                {a.sessionId && !compact && (
                  <Link to={`/chat/${a.sessionId}`} className="ml-auto text-xs text-[var(--color-accent)] hover:underline">
                    Open the chat
                  </Link>
                )}
              </div>
              <ErrorText error={decide.error} />
            </div>
          )}
          {!pending && a.status === "approved" && <p className="mt-3 text-xs text-[var(--muted)]">Your assistant carried on after the approval{a.sessionId ? " — see the chat" : ""}.</p>}
        </div>
      )}
    </Card>
  );
}

export function ApprovalsPage() {
  const { id } = useParams();
  const list = useQuery({ queryKey: ["approvals", "all"], queryFn: () => fetchApprovals(), refetchInterval: 20_000 });
  const rows = list.data?.approvals ?? [];
  const focus = id ? Number(id) : null;
  const pending = rows.filter((a) => a.status === "pending");
  const done = rows.filter((a) => a.status !== "pending");
  return (
    <>
      <PageHeader
        title="Approvals"
        description="Things your assistant prepared and is waiting for you to OK — like sharing something a colleague asked for, or sending an email. You can also decide from Slack, Discord or WhatsApp."
      />
      {list.isPending ? (
        <Spinner />
      ) : (
        <div className="space-y-6">
          <section className="space-y-3">
            <h2 className="text-sm font-medium text-[var(--muted)]">Waiting for you ({pending.length})</h2>
            {pending.length === 0 && (
              <Card className="flex flex-col items-center gap-2 px-4 py-8 text-center text-sm text-[var(--muted)]">
                <Icon name="checkCircle" size={22} className="text-emerald-600" />
                Nothing waiting. You're all caught up.
              </Card>
            )}
            {pending.map((a) => <ApprovalCard key={a.id} a={a} />)}
          </section>
          {done.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-[var(--muted)]">Decided</h2>
              {done.map((a) => <ApprovalCard key={a.id} a={a} compact={a.id !== focus} />)}
            </section>
          )}
          <ErrorText error={list.error} />
        </div>
      )}
    </>
  );
}
